/**
 * @page /admin/segmente
 * @description Segment-Konfiguration — Kundensegmente definieren, Vorschlaege
 *   verwalten, Simulation ausfuehren und Aenderungshistorie einsehen.
 * @features
 *   - Segment-Grenzen bearbeiten (min/max Pakete/Monat)
 *   - KI-Vorschlaege generieren und annehmen/ablehnen
 *   - Bulk-Aktionen fuer Vorschlaege
 *   - Simulation der Auswirkungen
 *   - Aenderungshistorie mit Begruendung
 * @api GET /api/segment-config, PUT /api/segment-config/:segment
 * @api GET /api/segment-vorschlaege, PUT /api/segment-vorschlaege/:id
 * @api POST /api/segment-config/vorschlaege-generieren
 * @api POST /api/segment-config/simulieren
 * @api POST /api/segment-vorschlaege/bulk
 * @api GET /api/segment-config/historie
 * @roles admin
 * @i18n segmentConfig
 */
import { useState, useEffect, useCallback } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';
const api = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, { credentials: 'include', ...opts });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
  if (data.error) throw new Error(data.error);
  return data;
};
const apiPut = (path, body) => api(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiPost = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

function fmtDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function SegmentConfig() {
  const { t } = useLanguage();
  const [configs, setConfigs] = useState([]);
  const [offeneCount, setOffeneCount] = useState(0);
  const [vorschlaege, setVorschlaege] = useState([]);
  const [historie, setHistorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editConfigs, setEditConfigs] = useState({});
  const [tab, setTab] = useTabParam('config');

  const grundLabels = {
    vorschlag_angenommen: t('segmentConfig.reasonAccepted'),
    manuell: t('segmentConfig.reasonManual'),
  };

  const load = useCallback(async () => {
    try {
      const [configRes, vRes, hRes] = await Promise.all([
        api('/api/segment-config'),
        api('/api/segment-vorschlaege'),
        api('/api/segment-config/historie?limit=20'),
      ]);
      setConfigs(configRes.configs || []);
      setOffeneCount(configRes.offene_vorschlaege || 0);
      setVorschlaege(Array.isArray(vRes) ? vRes : []);
      setHistorie(hRes.rows || []);
      // Initialize edit state
      const edits = {};
      for (const c of (configRes.configs || [])) {
        edits[c.segment] = { min_pakete_monat: c.min_pakete_monat, max_pakete_monat: c.max_pakete_monat };
      }
      setEditConfigs(edits);
    } catch (err) { setError(err.message || 'Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveConfig = async (segment) => {
    setSaving(true);
    setError(''); setSuccess('');
    try {
      const data = editConfigs[segment];
      const result = await apiPut(`/api/segment-config/${segment}`, {
        min_pakete_monat: data.min_pakete_monat === '' ? 0 : Number(data.min_pakete_monat),
        max_pakete_monat: data.max_pakete_monat === '' || data.max_pakete_monat === null ? null : Number(data.max_pakete_monat),
      });
      if (result.error) throw new Error(result.error);
      setSuccess(`${t('segmentConfig.segmentSaved')} ${segment}`);
      load();
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(''); setSuccess(''); setSimResult(null);
    try {
      const result = await apiPost('/api/segment-config/vorschlaege-generieren');
      if (result.error) throw new Error(result.error);
      setSuccess(`${result.count} ${t('segmentConfig.suggestionsGenerated')}`);
      load();
    } catch (err) { setError(err.message); }
    setGenerating(false);
  };

  const handleSimulate = async () => {
    setSimulating(true);
    setError(''); setSimResult(null);
    try {
      const result = await apiPost('/api/segment-config/simulieren');
      if (result.error) throw new Error(result.error);
      setSimResult(result);
    } catch (err) { setError(err.message); }
    setSimulating(false);
  };

  const handleEntscheidung = async (id, entscheidung) => {
    setError('');
    try {
      const result = await apiPut(`/api/segment-vorschlaege/${id}`, { entscheidung });
      if (result.error) throw new Error(result.error);
      load();
    } catch (err) { setError(err.message); }
  };

  const handleBulk = async (entscheidung) => {
    if (!confirm(t(entscheidung === 'angenommen' ? 'segmentConfig.confirmBulkAccept' : 'segmentConfig.confirmBulkReject').replace('{n}', vorschlaege.length))) return;
    setError('');
    try {
      const ids = vorschlaege.map(v => v.id);
      const result = await apiPost('/api/segment-vorschlaege/bulk', { ids, entscheidung });
      if (result.error) throw new Error(result.error);
      setSuccess(`${result.count} ${t('segmentConfig.suggestionsProcessed')}`);
      load();
    } catch (err) { setError(err.message); }
  };

  const segColor = { A: '#338edc', B: '#4D9CE0', C: '#F59E0B' };
  const segBg = { A: 'bg-primary-500/10 text-primary-400', B: 'bg-purple-500/10 text-purple-400', C: 'bg-amber-500/10 text-amber-400' };
  const inp = "w-full bg-surface-inset border border-line-input rounded-lg px-3 py-2 text-sm text-content font-mono placeholder-content-dim focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-colors";

  if (loading) return (
    <div className="p-6 flex justify-center py-20">
      <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-lg font-bold text-content">{t('segmentConfig.title')}</h1>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('segmentConfig.subtitle')}</p>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5 text-xs text-red-400 mb-4">{error}</div>}
      {success && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-2.5 text-xs text-emerald-400 mb-4">{success}</div>}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-surface-card p-1 rounded-card w-fit shadow-card">
        {[
          ['config', t('segmentConfig.tabConfig')],
          ['vorschlaege', `${t('segmentConfig.tabSuggestions')} (${vorschlaege.length})`],
          ['historie', t('segmentConfig.tabHistory')],
        ].map(([key, lbl]) => (
          <button key={key} onClick={() => setTab(key)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${tab === key ? 'bg-white dark:bg-surface-card shadow-sm text-content font-semibold' : 'text-[var(--text-secondary)] hover:text-content-secondary'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {/* TAB: Konfiguration */}
      {tab === 'config' && (
        <div className="space-y-4">
          {/* Segment Cards */}
          {configs.map(c => (
            <div key={c.segment} className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-line">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.farbe }} />
                  <span className="text-sm font-bold text-content">{c.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${segBg[c.segment]}`}>
                    {c.anzahl_kunden} {t('segmentConfig.customers')}
                  </span>
                </div>
                <span className="text-[10px] text-content-tertiary">{c.picking_modus === 'kundenspezifisch' ? t('segmentConfig.ownWaves') : t('segmentConfig.mixedWaves')}</span>
              </div>
              <div className="px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="block text-[10px] font-medium text-content-tertiary mb-1">{t('segmentConfig.fromPackagesPerMonth')}</label>
                    <input
                      type="number"
                      value={editConfigs[c.segment]?.min_pakete_monat ?? ''}
                      onChange={e => setEditConfigs(prev => ({ ...prev, [c.segment]: { ...prev[c.segment], min_pakete_monat: e.target.value } }))}
                      className={inp}
                      min="0"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] font-medium text-content-tertiary mb-1">{t('segmentConfig.toPackagesPerMonth')}</label>
                    <input
                      type="number"
                      value={editConfigs[c.segment]?.max_pakete_monat ?? ''}
                      onChange={e => setEditConfigs(prev => ({ ...prev, [c.segment]: { ...prev[c.segment], max_pakete_monat: e.target.value } }))}
                      className={inp}
                      placeholder={t('segmentConfig.unlimited')}
                    />
                  </div>
                  <div className="flex-shrink-0 pt-4">
                    <button
                      onClick={() => handleSaveConfig(c.segment)}
                      disabled={saving}
                      className="px-3 py-2 bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {t('common.save')}
                    </button>
                  </div>
                </div>
                {c.beschreibung && <p className="text-[10px] text-content-tertiary mt-2">{c.beschreibung}</p>}
              </div>
            </div>
          ))}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="px-4 py-2.5 bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              {generating ? t('segmentConfig.generating') : t('segmentConfig.generateSuggestions')}
            </button>
            <button
              onClick={handleSimulate}
              disabled={simulating}
              className="px-4 py-2.5 bg-surface-inset hover:bg-surface-active text-content-secondary text-xs font-medium rounded-lg border border-line transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              {simulating ? t('segmentConfig.simulating') : t('segmentConfig.simulate')}
            </button>
          </div>

          {/* Simulation Results */}
          {simResult && (
            <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                <span className="text-xs font-semibold text-content">{t('segmentConfig.simulationResult')}: {simResult.count} {t('segmentConfig.changes')}</span>
                <button onClick={() => setSimResult(null)} className="ml-auto text-content-tertiary hover:text-[var(--text-secondary)] text-xs">{t('common.close')}</button>
              </div>
              {simResult.count === 0 ? (
                <p className="text-xs text-[var(--text-secondary)]">{t('segmentConfig.allCorrectlySegmented')}</p>
              ) : (
                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                  {simResult.vorschlaege.map((v, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-surface-inset/50 text-xs">
                      <span className="font-medium text-content truncate flex-1 mr-2">{v.kunde_name}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${segBg[v.aktuelles_segment]}`}>{v.aktuelles_segment}</span>
                        <span className="text-content-tertiary">&rarr;</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${segBg[v.vorgeschlagenes_segment]}`}>{v.vorgeschlagenes_segment}</span>
                        <span className="text-content-tertiary font-mono">Ø {v.avg_pakete_3m?.toLocaleString()} Pak</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TAB: Vorschläge */}
      {tab === 'vorschlaege' && (
        <div>
          {vorschlaege.length === 0 ? (
            <div className="bg-surface-card border border-line rounded-card p-8 text-center shadow-card">
              <svg className="w-10 h-10 mx-auto text-content-tertiary mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <p className="text-sm text-[var(--text-secondary)]">{t('segmentConfig.noOpenSuggestions')}</p>
              <p className="text-xs text-content-tertiary mt-1">{t('segmentConfig.allCorrectlySegmented')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
                <div className="divide-y divide-line">
                  {vorschlaege.map(v => (
                    <div key={v.id} className="px-5 py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-sm font-medium text-content truncate">{v.kunde_name}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${segBg[v.aktuelles_segment]}`}>{v.aktuelles_segment}</span>
                          <span className="text-content-tertiary text-xs">&rarr;</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${segBg[v.vorgeschlagenes_segment]}`}>{v.vorgeschlagenes_segment}</span>
                        </div>
                        <span className="text-[10px] text-content-tertiary font-mono flex-shrink-0">Ø {v.avg_pakete_3m?.toLocaleString()} Pak</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => handleEntscheidung(v.id, 'angenommen')} className="px-2 py-1 text-[10px] font-medium text-emerald-400 hover:bg-emerald-500/10 rounded transition-colors" title={t('segmentConfig.accept')}>{t('segmentConfig.yes')}</button>
                        <button onClick={() => handleEntscheidung(v.id, 'abgelehnt')} className="px-2 py-1 text-[10px] font-medium text-red-400 hover:bg-red-500/10 rounded transition-colors" title={t('segmentConfig.reject')}>{t('segmentConfig.no')}</button>
                        <button onClick={() => handleEntscheidung(v.id, 'erinnern')} className="px-2 py-1 text-[10px] font-medium text-amber-400 hover:bg-amber-500/10 rounded transition-colors" title={t('segmentConfig.remindIn30Days')}>
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Bulk Actions */}
              <div className="flex items-center gap-3">
                <button onClick={() => handleBulk('angenommen')} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-lg transition-colors">
                  {t('segmentConfig.acceptAll')} ({vorschlaege.length})
                </button>
                <button onClick={() => handleBulk('abgelehnt')} className="px-3 py-2 bg-surface-inset hover:bg-surface-active text-[var(--text-secondary)] text-xs font-medium rounded-lg border border-line transition-colors">
                  {t('segmentConfig.rejectAll')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: Historie */}
      {tab === 'historie' && (
        <div>
          {historie.length === 0 ? (
            <div className="bg-surface-card border border-line rounded-card p-8 text-center shadow-card">
              <p className="text-sm text-[var(--text-secondary)]">{t('segmentConfig.noHistory')}</p>
            </div>
          ) : (
            <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('segmentConfig.date')}</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('segmentConfig.customer')}</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('segmentConfig.change')}</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('segmentConfig.reason')}</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('segmentConfig.by')}</th>
                  </tr>
                </thead>
                <tbody>
                  {historie.map(h => (
                    <tr key={h.id} className="border-b border-line hover:bg-surface-card">
                      <td className="px-4 py-2.5 text-xs text-[var(--text-secondary)]">{fmtDate(h.erstellt_am)}</td>
                      <td className="px-4 py-2.5 text-xs font-medium text-content">{h.kunde_name}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${segBg[h.von_segment]}`}>{h.von_segment}</span>
                          <span className="text-content-tertiary text-xs">&rarr;</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${segBg[h.nach_segment]}`}>{h.nach_segment}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-[10px] text-[var(--text-secondary)]">{grundLabels[h.grund] || h.grund}</td>
                      <td className="px-4 py-2.5 text-xs text-[var(--text-secondary)]">{h.erstellt_von_name || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
